import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/common/AppLayout';
import Button from '../components/common/Button';
import StatusBadge, { PhaseBadge } from '../components/common/StatusBadge';
import Spinner from '../components/common/Spinner';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  PROJECT_PHASE,
  PHASE_LABELS,
  WORKFLOW_STATUS,
  WORKFLOW_STATUS_LABELS
} from '../utils/constants';
import {
  formatCurrency,
  formatDate,
  formatProjectNumber,
  formatUserName,
  formatDeadline
} from '../utils/formatters';
import { getProjectSid, hasProjectSid } from '../utils/projectHelpers';

function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    projects,
    loading,
    error,
    filters,
    pagination,
    fetchProjects,
    updateFilters,
    clearFilters,
    setPagination
  } = useProjects();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedProjects, setSelectedProjects] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [availableSids, setAvailableSids] = useState([]);
  const [showBulkSidModal, setShowBulkSidModal] = useState(false);
  const [bulkSidId, setBulkSidId] = useState('');
  const [pageSize, setPageSize] = useState(20);

  // Fetch projects and SIDs on mount
  useEffect(() => {
    fetchProjects();
    fetchAvailableSids();
  }, [filters, pagination.page]);

  async function fetchAvailableSids() {
    try {
      // Fetch both SIDs and ASIDs separately since backend doesn't support comma-separated roles
      const [sidsResponse, asidsResponse] = await Promise.all([
        api.get('/users?role=sid'),
        api.get('/users?role=asid')
      ]);

      const allSids = [
        ...(sidsResponse.data.users || []),
        ...(asidsResponse.data.users || [])
      ];

      // Filter out resigned users and sort by role (SID first, then ASID)
      const activeSids = allSids
        .filter(user => !user.isResigned)
        .sort((a, b) => {
          if (a.role === 'sid' && b.role === 'asid') return -1;
          if (a.role === 'asid' && b.role === 'sid') return 1;
          return 0;
        });

      setAvailableSids(activeSids);
    } catch (error) {
      console.error('Fetch SIDs error:', error);
      toast.error('Failed to load available SIDs');
    }
  }

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ search: searchQuery });
  };

  // Handle phase filter
  const handlePhaseFilter = (phase) => {
    setSelectedPhase(phase);
    if (phase === 'all') {
      updateFilters({ currentPhase: '' });
    } else {
      updateFilters({ currentPhase: phase });
    }
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    if (status === 'all') {
      updateFilters({ workflowStatus: '' });
    } else {
      updateFilters({ workflowStatus: status });
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle nested fields
    if (sortField === 'sid') {
      aVal = formatUserName(getProjectSid(a));
      bVal = formatUserName(getProjectSid(b));
    }

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Handle row selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProjects(new Set(projects.map(p => p.id)));
    } else {
      setSelectedProjects(new Set());
    }
  };

  const handleSelectRow = (projectId) => {
    const newSelected = new Set(selectedProjects);
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId);
    } else {
      newSelected.add(projectId);
    }
    setSelectedProjects(newSelected);
  };

  // Handle bulk SID assignment
  const handleBulkAssignSid = async () => {
    if (!bulkSidId) {
      toast.error('Please select a SID');
      return;
    }

    try {
      const promises = Array.from(selectedProjects).map(projectId =>
        api.patch(`/design-projects/${projectId}/assign-sid`, { sidId: bulkSidId })
      );

      await Promise.all(promises);
      toast.success(`Assigned SID to ${selectedProjects.size} project(s)`);
      setShowBulkSidModal(false);
      setBulkSidId('');
      setSelectedProjects(new Set());
      fetchProjects();
    } catch (error) {
      console.error('Bulk SID assignment error:', error);
      toast.error('Failed to assign SID to some projects');
    }
  };

  // Get project statistics
  const getProjectStats = () => {
    const stats = {
      total: projects.length,
      unassignedSid: projects.filter(p => !hasProjectSid(p)).length,
      urgent: projects.filter(p => {
        if (!p.currentDeadline) return false;
        const deadline = new Date(p.currentDeadline);
        const now = new Date();
        const daysUntil = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
        return daysUntil <= 3 && daysUntil >= 0;
      }).length,
      overdue: projects.filter(p => {
        if (!p.currentDeadline) return false;
        const deadline = new Date(p.currentDeadline);
        return deadline < new Date();
      }).length,
      pending2DReview: projects.filter(p =>
        p.workflowStatus === WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW
      ).length,
      pending3DReview: projects.filter(p =>
        p.workflowStatus === WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW
      ).length,
      readyForSigning: projects.filter(p =>
        p.workflowStatus === WORKFLOW_STATUS.READY_FOR_SIGNING
      ).length
    };
    return stats;
  };

  const stats = getProjectStats();

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <AppLayout>
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Projects Management</h1>
            <p className="page-subtitle">
              Track and manage all interior design projects
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {selectedProjects.size > 0 && (
              <Button
                variant="primary"
                onClick={() => setShowBulkSidModal(true)}
              >
                Assign SID ({selectedProjects.size})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handlePhaseFilter('all')}>
          <p className="text-sm text-gray-600 mb-1">Total Projects</p>
          <p className="text-2xl font-bold text-tenq-gold">{stats.total}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {
          updateFilters({ unassignedSid: true });
        }}>
          <p className="text-sm text-gray-600 mb-1">Unassigned SID</p>
          <p className="text-2xl font-bold text-orange-600">{stats.unassignedSid}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow">
          <p className="text-sm text-gray-600 mb-1">Urgent</p>
          <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow">
          <p className="text-sm text-gray-600 mb-1">Overdue</p>
          <p className="text-2xl font-bold text-red-700">{stats.overdue}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleStatusFilter(WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW)}>
          <p className="text-sm text-gray-600 mb-1">2D Review</p>
          <p className="text-2xl font-bold text-purple-600">{stats.pending2DReview}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleStatusFilter(WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW)}>
          <p className="text-sm text-gray-600 mb-1">3D Review</p>
          <p className="text-2xl font-bold text-blue-600">{stats.pending3DReview}</p>
        </div>

        <div className="card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleStatusFilter(WORKFLOW_STATUS.READY_FOR_SIGNING)}>
          <p className="text-sm text-gray-600 mb-1">Ready to Sign</p>
          <p className="text-2xl font-bold text-green-600">{stats.readyForSigning}</p>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card mb-6">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by project number, client name, or SID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tenq-gold focus:border-tenq-gold"
              />
              <Button type="submit" variant="primary">
                Search
              </Button>
              {(searchQuery || selectedPhase !== 'all' || selectedStatus !== 'all') && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedPhase('all');
                    setSelectedStatus('all');
                    clearFilters();
                  }}
                >
                  Clear All
                </Button>
              )}
            </div>
          </form>

          {/* Phase Filters */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Filter by Phase:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handlePhaseFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedPhase === 'all'
                    ? 'bg-tenq-gold text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {Object.keys(PROJECT_PHASE).map(phaseKey => {
                const phase = PROJECT_PHASE[phaseKey];
                return (
                  <button
                    key={phase}
                    onClick={() => handlePhaseFilter(phase)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      selectedPhase === phase
                        ? 'bg-tenq-gold text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {PHASE_LABELS[phase]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status Filters */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Quick Status Filters:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === 'all'
                    ? 'bg-tenq-gold text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Statuses
              </button>
              <button
                onClick={() => handleStatusFilter(WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW
                    ? 'bg-tenq-gold text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                2D Pending Review
              </button>
              <button
                onClick={() => handleStatusFilter(WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW
                    ? 'bg-tenq-gold text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                3D Pending Review
              </button>
              <button
                onClick={() => handleStatusFilter(WORKFLOW_STATUS.READY_FOR_SIGNING)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === WORKFLOW_STATUS.READY_FOR_SIGNING
                    ? 'bg-tenq-gold text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ready for Signing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Controls */}
      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600">
              Showing {sortedProjects.length} of {pagination.total} projects
            </p>
            {selectedProjects.size > 0 && (
              <p className="text-sm font-medium text-tenq-gold">
                {selectedProjects.size} selected
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Rows per page:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }));
              }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      ) : projects.length === 0 ? (
        <div className="card text-center py-12">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects Found</h3>
          <p className="text-gray-600">
            {searchQuery || selectedPhase !== 'all' || selectedStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'No projects have been created yet'}
          </p>
        </div>
      ) : (
        <>
          {/* Projects Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedProjects.size === projects.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-tenq-gold focus:ring-tenq-gold"
                      />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('projectNumber')}
                    >
                      Project #
                      <SortIndicator field="projectNumber" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('clientName')}
                    >
                      Client
                      <SortIndicator field="clientName" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phase
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('sid')}
                    >
                      Assigned SID
                      <SortIndicator field="sid" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('currentPackagePrice')}
                    >
                      Package Price
                      <SortIndicator field="currentPackagePrice" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('currentDeadline')}
                    >
                      Deadline
                      <SortIndicator field="currentDeadline" />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}
                    >
                      Created
                      <SortIndicator field="createdAt" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedProjects.map((project) => {
                    const deadlineInfo = formatDeadline(project.currentDeadline);
                    const isUrgent = deadlineInfo.urgency === 'high' || deadlineInfo.urgency === 'critical';
                    const isOverdue = deadlineInfo.urgency === 'overdue';

                    return (
                      <tr
                        key={project.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          isOverdue ? 'bg-red-50' : isUrgent ? 'bg-orange-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedProjects.has(project.id)}
                            onChange={() => handleSelectRow(project.id)}
                            className="rounded border-gray-300 text-tenq-gold focus:ring-tenq-gold"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/projects/${project.id}`)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {formatProjectNumber(project.projectNumber)}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {project.clientName}
                            </div>
                            {project.clientLocation && (
                              <div className="text-gray-500 text-xs">
                                {project.clientLocation.length > 30
                                  ? `${project.clientLocation.substring(0, 30)}...`
                                  : project.clientLocation}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <PhaseBadge phase={project.currentPhase} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={project.workflowStatus} size="sm" />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {hasProjectSid(project) ? (
                              formatUserName(getProjectSid(project))
                            ) : (
                              <span className="text-orange-600 font-medium">Unassigned</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {project.currentPackagePrice
                            ? formatCurrency(project.currentPackagePrice)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {project.currentDeadline ? (
                            <div className="text-sm">
                              <div className={`font-medium ${
                                isOverdue ? 'text-red-700' :
                                isUrgent ? 'text-orange-700' : 'text-gray-900'
                              }`}>
                                {deadlineInfo.text}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No deadline</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(project.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/projects/${project.id}`)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
                  // Show first 3, last 3, and current page +/- 1
                  const page = i + 1;
                  const shouldShow =
                    page <= 3 ||
                    page > pagination.totalPages - 3 ||
                    Math.abs(page - pagination.page) <= 1;

                  if (!shouldShow && (page === 4 || page === pagination.totalPages - 3)) {
                    return <span key={page} className="px-2 py-2 text-gray-500">...</span>;
                  }

                  if (!shouldShow) return null;

                  return (
                    <button
                      key={page}
                      onClick={() => setPagination(prev => ({ ...prev, page }))}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        pagination.page === page
                          ? 'bg-tenq-gold text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Bulk SID Assignment Modal */}
      {showBulkSidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Assign SID to Selected Projects</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to assign a SID to {selectedProjects.size} project(s).
            </p>
            <div className="mb-6">
              <label className="label">Select SID/ASID</label>
              <select
                value={bulkSidId}
                onChange={(e) => setBulkSidId(e.target.value)}
                className="input w-full"
              >
                <option value="">-- Select SID --</option>
                {availableSids.length === 0 ? (
                  <option disabled>No SIDs available</option>
                ) : (
                  availableSids.map((sid) => (
                    <option key={sid.id} value={sid.id}>
                      {sid.fullName || sid.nickname} ({sid.role.toUpperCase()})
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkSidModal(false);
                  setBulkSidId('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkAssignSid}
                disabled={!bulkSidId}
              >
                Assign SID
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default ProjectsPage;
